/**
 * Data Discovery Service
 *
 * Lightweight frontend service that composes existing schema and template APIs
 * to produce DataDiscoveryResult and SchemaExplorationResult for the data panel.
 */

import { schemaApi, Schema, SchemaField } from './schemaApi';
import { queryTemplateApi } from './queryTemplateApi';
import { getImportedSchemaBinding } from './csvDatasetImportApi';
import { getCurrentAuthTokens } from './auth-session';
import { useAuthStore } from '@/stores/auth/auth-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { logger } from '@/utils/logger';
import type { AxiosRequestConfig } from 'axios';
import type {
  DataDiscoveryResult,
  SchemaCategory,
  SchemaPreview,
  SchemaExplorationResult,
  SchemaFieldInfo,
  QueryTemplatePreview,
} from '@/types/chartDataSource';

const NUMERIC_TYPES = new Set(['number']);
const TIME_TYPES = new Set(['date', 'datetime']);
const DISCOVERY_CONTEXT_WAIT_MS = 3000;

function hasAuthenticatedAccessToken(): boolean {
  const state = useAuthStore.getState();
  return state.isAuthenticated && Boolean(getCurrentAuthTokens()?.accessToken);
}

async function waitForAuthenticatedAccessToken(): Promise<void> {
  if (hasAuthenticatedAccessToken()) {
    return;
  }

  await new Promise<void>((resolve) => {
    const timeout = setTimeout(() => {
      unsubscribe();
      resolve();
    }, DISCOVERY_CONTEXT_WAIT_MS);

    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.isAuthenticated && Boolean(state.tokens?.accessToken)) {
        clearTimeout(timeout);
        unsubscribe();
        resolve();
      }
    });
  });

  if (!hasAuthenticatedAccessToken()) {
    throw new Error('Sign in before browsing data sources.');
  }
}

function toSchemaPreview(schema: Schema): SchemaPreview {
  const importedBinding = getImportedSchemaBinding(schema.id);
  const groupId = schema.groupId || importedBinding?.groupId;
  const numericFieldCount = schema.fields.filter((f) => NUMERIC_TYPES.has(f.type)).length;
  const timeFieldCount = schema.fields.filter((f) => TIME_TYPES.has(f.type)).length;

  return {
    id: schema.id,
    name: schema.name,
    displayName: schema.displayName || schema.name,
    description: schema.description,
    ...(groupId ? { groupId } : {}),
    fieldCount: schema.fields.length,
    numericFieldCount,
    timeFieldCount,
    hasTimeFields: timeFieldCount > 0,
    fields: schema.fields.map((field) => field.name),
    tags: [],
  };
}

function getImportedSchemaBindingForIds(schemaIds: string[]) {
  for (const schemaId of schemaIds) {
    const importedBinding = getImportedSchemaBinding(schemaId);
    if (importedBinding) {
      return importedBinding;
    }
  }

  return undefined;
}

async function resolveBindableSchema(
  schema: Schema,
  axiosConfig?: AxiosRequestConfig,
): Promise<Schema | null> {
  const importedBinding = getImportedSchemaBinding(schema.id);
  if (schema.groupId) {
    return schema;
  }
  if (importedBinding?.groupId) {
    return {
      ...schema,
      groupId: importedBinding.groupId,
    };
  }

  const memberships = await schemaApi.getSchemaGroups(schema.id, axiosConfig).catch(() => []);
  const groupId = memberships[0]?.groupId;
  if (!groupId) {
    return null;
  }

  return {
    ...schema,
    groupId,
  };
}

async function resolveBindableSchemas(
  schemas: Schema[],
  axiosConfig?: AxiosRequestConfig,
): Promise<Schema[]> {
  const resolvedSchemas = await Promise.all(
    schemas.map((schema) => resolveBindableSchema(schema, axiosConfig)),
  );
  const bindableSchemas = resolvedSchemas.filter((schema): schema is Schema => schema !== null);

  return bindableSchemas;
}

function groupSchemasByCategory(schemas: Schema[]): SchemaCategory[] {
  const categoryMap = new Map<string, Schema[]>();

  for (const schema of schemas) {
    const category = schema.displayName?.split(' ')[0] || 'General';
    const existing = categoryMap.get(category) || [];
    existing.push(schema);
    categoryMap.set(category, existing);
  }

  // If all schemas fell into unique single-word categories, just use "All Data Sources"
  if (categoryMap.size > schemas.length * 0.8) {
    return [
      {
        name: 'All Data Sources',
        schemaCount: schemas.length,
        schemas: schemas.map(toSchemaPreview),
      },
    ];
  }

  return Array.from(categoryMap.entries()).map(([name, group]) => ({
    name,
    schemaCount: group.length,
    schemas: group.map(toSchemaPreview),
  }));
}

function generateSuggestions(schemas: Schema[]): string[] {
  const suggestions: string[] = [];
  for (const schema of schemas.slice(0, 3)) {
    const name = schema.displayName || schema.name;
    suggestions.push(`Show ${name} data over the last 7 days`);
  }
  if (suggestions.length === 0) {
    suggestions.push('Browse your data sources to connect to this chart');
  }
  return suggestions;
}

export async function discoverDataSources(): Promise<DataDiscoveryResult> {
  await waitForAuthenticatedAccessToken();

  // Wait for org context AND organizations list to be populated.
  // currentContext is restored from localStorage immediately, but the organizations
  // array requires fetchOrganizations() to complete. Both are needed so the API
  // interceptors can send X-Organization-Id (org._id lookup requires the array).
  const orgState = useOrganizationStore.getState();
  const isReady = (s: typeof orgState) =>
    s.currentContext?.type === 'organization' && s.organizations.length > 0;

  if (!isReady(orgState)) {
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => { unsubscribe(); resolve(); }, 3000);
      const unsubscribe = useOrganizationStore.subscribe((state) => {
        if (isReady(state)) {
          clearTimeout(timeout);
          unsubscribe();
          resolve();
        }
      });
    });
  }

  const [schemasResult, templatesResult] = await Promise.all([
    schemaApi.listSchemas({ status: 'published', limit: 100 }),
    queryTemplateApi.list({ limit: 50 }).catch(() => {
      return { templates: [], total: 0 };
    }),
  ]);

  const schemas = await resolveBindableSchemas(schemasResult.data);
  const categories = groupSchemasByCategory(schemas);

  const templates: QueryTemplatePreview[] = templatesResult.templates.map((t) => ({
    id: t.id || t._id || '',
    name: t.name,
    description: t.description,
    usageCount: t.usageCount || 0,
    lastUsedAt: t.lastUsedAt,
    tags: t.tags,
    measures: t.query?.measures?.map((m) => ({
      field: m.field,
      aggregation: m.aggregation,
      alias: m.alias,
    })),
    schemaId: t.query?.schemaId,
    groupId: t.query?.groupId,
  }));

  return {
    totalSources: schemas.length,
    categories,
    suggestedQueries: generateSuggestions(schemas),
    templates,
    templateCount: templatesResult.total,
  };
}

/**
 * Discover data sources from ALL organizations the user belongs to.
 * Used in personal space for cross-org data source selection.
 * Each org's schemas are fetched in parallel with per-request X-Organization-Id headers.
 */
export async function discoverCrossOrgDataSources(): Promise<DataDiscoveryResult> {
  await waitForAuthenticatedAccessToken();

  const orgs = useOrganizationStore.getState().organizations;

  if (orgs.length === 0) {
    return {
      totalSources: 0,
      categories: [],
      suggestedQueries: ['Join an organization to see data sources'],
    };
  }

  // Fetch schemas and templates from each org in parallel, logging per-org failures.
  const results = await Promise.all(
    orgs.map(async (org) => {
      try {
        const headerConfig: AxiosRequestConfig = {
          headers: { 'X-Organization-Id': org._id },
        };
        const [schemasResult, templatesResult] = await Promise.all([
          schemaApi.listSchemas({ status: 'published', limit: 100 }, headerConfig),
          queryTemplateApi.list({ limit: 50 }, headerConfig),
        ]);
        const schemas = await resolveBindableSchemas(schemasResult.data, headerConfig);
        return {
          org,
          schemas,
          templates: templatesResult.templates,
        };
      } catch (error) {
        logger.warn('Cross-org data source discovery failed', {
          orgId: org._id,
          orgSlug: org.slug,
          error: error instanceof Error ? error.message : String(error),
        });
        return null;
      }
    })
  );

  // Build one category per org (schemas grouped by org name)
  const allCategories: SchemaCategory[] = [];
  const allTemplates: QueryTemplatePreview[] = [];
  let totalSources = 0;

  for (const result of results) {
    if (!result) continue;
    const { org, schemas, templates } = result;
    if (!schemas.length && !templates.length) continue;

    totalSources += schemas.length;

    allCategories.push({
      name: org.name,
      description: `Data sources from ${org.name}`,
      schemaCount: schemas.length,
      schemas: schemas.map((s) => ({
        ...toSchemaPreview(s),
        organizationId: org._id,
        organizationName: org.name,
      })),
    });

    for (const t of templates) {
      allTemplates.push({
        id: t.id || t._id || '',
        name: t.name,
        description: t.description,
        usageCount: t.usageCount || 0,
        lastUsedAt: t.lastUsedAt,
        tags: t.tags,
        measures: t.query?.measures?.map((m) => ({
          field: m.field,
          aggregation: m.aggregation,
          alias: m.alias,
        })),
        schemaId: t.query?.schemaId,
        groupId: t.query?.groupId,
        organizationId: org._id,
        organizationName: org.name,
      });
    }
  }

  return {
    totalSources,
    categories: allCategories,
    suggestedQueries:
      totalSources > 0
        ? ['Select a data source from any organization']
        : ['No data sources found across your organizations'],
    templates: allTemplates,
    templateCount: allTemplates.length,
  };
}

function toFieldInfo(field: SchemaField): SchemaFieldInfo {
  let type: SchemaFieldInfo['type'] = 'string';
  if (field.type === 'date' || field.type === 'datetime') {
    type = field.type;
  } else if (NUMERIC_TYPES.has(field.type)) {
    type = 'number';
  } else if (field.type === 'boolean') {
    type = 'boolean';
  }

  return {
    name: field.name,
    displayName: field.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    type,
    description: field.description,
    isRequired: field.required,
  };
}

export async function exploreSchema(
  schemaId: string,
  axiosConfig?: AxiosRequestConfig
): Promise<SchemaExplorationResult> {
  const schema = await schemaApi.getSchema(schemaId, axiosConfig);
  const schemaIdCandidates = Array.from(new Set([schema.id, schemaId].filter(Boolean)));
  const importedBinding = getImportedSchemaBindingForIds(schemaIdCandidates);
  const schemaMemberships =
    schema.groupId || importedBinding?.groupId
      ? []
      : await schemaApi.getSchemaGroups(schemaId, axiosConfig).catch(() => []);
  const resolvedGroupId =
    schema.groupId ||
    importedBinding?.groupId ||
    schemaMemberships[0]?.groupId;
  const fields = schema.fields.map(toFieldInfo);

  const chartableFields = fields.filter((f) => f.type === 'number');
  const timeFields = fields.filter((f) => f.type === 'date' || f.type === 'datetime');
  const otherFields = fields.filter(
    (f) => f.type !== 'number' && f.type !== 'date' && f.type !== 'datetime'
  );

  const name = schema.displayName || schema.name;
  const sampleQueries: string[] = [];
  if (chartableFields.length > 0) {
    sampleQueries.push(`Show ${chartableFields[0].displayName || chartableFields[0].name} from ${name}`);
  }
  if (timeFields.length > 0 && chartableFields.length > 0) {
    sampleQueries.push(`Plot ${name} over time`);
  }
  sampleQueries.push(`Get the latest ${name} reading`);

  return {
    schema: {
      id: schema.id || schemaId,
      name: schema.name,
      displayName: schema.displayName,
      description: schema.description,
      ...(resolvedGroupId ? { groupId: resolvedGroupId } : {}),
      fields,
      timeFields: timeFields.map((f) => f.name),
      numericFields: chartableFields.map((f) => f.name),
    },
    chartableFields,
    timeFields,
    otherFields,
    sampleQueries,
  };
}
